import { overrideRedirectUri } from 'utils/url';
import type { NavigateFunction } from 'react-router-dom';

/**
 * URL parameter marking that the stored Helios mapping was rejected and the
 * user must remap. Set by the global API error middleware, consumed by
 * UserHeliosModulesAgendaList to force the mapping modal open.
 */
export const HELIOS_MAPPING_ERROR_PARAM = 'heliosMappingError';

/**
 * Identity of the rejected mapping carried through recovery. Pages hosting
 * Helios-backed components outside Helios routes (e.g. Flow record external
 * items) use it to open remapping for the datasource that actually failed.
 */
export const HELIOS_MAPPING_TEMPLATE_PARAM = 'heliosMappingTemplate';
export const HELIOS_MAPPING_DATASOURCE_PARAM = 'heliosMappingDatasource';

export interface MappingRecoveryIdentity {
  templateId?: string;
  datasourceId?: string;
}

/**
 * Backend error codes that invalidate the stored Helios mapping and should
 * trigger the remapping flow (invalid_grant for dead OAuth refresh tokens,
 * *_mapping_required when the server already removed the mapping).
 */
export const HELIOS_MAPPING_ERROR_CODES = new Set(['invalid_grant', 'google_mapping_required', 'helios_mapping_required']);

/**
 * URL of the mapping-recovery redirect: the current URL with the error flag
 * set. Helios record detail/edit routes are shortened to their agenda index —
 * the remapping modal lives there, reloading the editor would just refire the
 * rejected query. Other pages (e.g. Flow records) keep their path so the
 * component that issued the failed request can recover in place.
 */
export const buildMappingRecoveryUrl = (href: string, identity?: MappingRecoveryIdentity): string => {
  const url = new URL(href);
  const agendaPathMatch = url.pathname.match(/^(.+\/Helios\/[^/]+)\/r\//);
  if (agendaPathMatch) {
    url.pathname = agendaPathMatch[1];
  }
  url.searchParams.set(HELIOS_MAPPING_ERROR_PARAM, 'true');
  if (identity?.templateId) {
    url.searchParams.set(HELIOS_MAPPING_TEMPLATE_PARAM, identity.templateId);
  }
  if (identity?.datasourceId) {
    url.searchParams.set(HELIOS_MAPPING_DATASOURCE_PARAM, identity.datasourceId);
  }
  return url.toString();
};

/**
 * Routes that can display mapping recovery: Helios agenda screens consume the
 * flag themselves, Flow agenda/record screens host RecordsExternalItem, and
 * user settings renders record sections the same way. Other /user screens —
 * the overview and module dashboards embed Helios tables via ActivitiesWidget
 * without a recovery consumer — would get a dead redirect that only suppresses
 * the error, so they keep normal error reporting instead.
 */
const MAPPING_RECOVERY_PATH_PATTERN = /^\/user\/modules\/[^/]+(?:\/m\/[^/]+)*\/(?:Helios|Flow)(?:\/|$)|^\/user\/settings(?:\/|$)/;

export const isMappingRecoverySupportedPath = (pathname: string): boolean => MAPPING_RECOVERY_PATH_PATTERN.test(pathname);

type AppNavigate = (to: { pathname: string; search: string; hash: string }, options: { replace?: boolean }) => void;

let appNavigate: AppNavigate | null = null;

/**
 * Lets non-React code (the API error middleware) navigate in-app without a
 * document reload — reloading would drop mounted forms' unsaved state.
 * Registered by a Router-level component; while unavailable (early bootstrap)
 * callers fall back to location.replace.
 */
export const registerAppNavigation = (navigate: AppNavigate): (() => void) => {
  appNavigate = navigate;
  return () => {
    appNavigate = null;
  };
};

export const redirectToMappingRecovery = (identity?: MappingRecoveryIdentity) => {
  const recoveryUrl = new URL(buildMappingRecoveryUrl(window.location.href, identity));
  if (appNavigate) {
    appNavigate({ pathname: recoveryUrl.pathname, search: recoveryUrl.search, hash: recoveryUrl.hash }, { replace: true });
    return;
  }
  window.location.replace(recoveryUrl.toString());
};

/**
 * The current URL with the recovery params removed — used before reloading
 * after a successful remap so the page lands on the content, not back in the
 * modal.
 */
export const stripMappingErrorParam = (href: string): string => {
  const url = new URL(href);
  url.searchParams.delete(HELIOS_MAPPING_ERROR_PARAM);
  url.searchParams.delete(HELIOS_MAPPING_TEMPLATE_PARAM);
  url.searchParams.delete(HELIOS_MAPPING_DATASOURCE_PARAM);
  return url.toString();
};

/**
 * Where cancelling recovery lands: the owning module detail. The stored
 * mapping can still report isMapped while its credentials are rejected, so
 * staying on (or returning to) a credential-dependent screen would refire the
 * rejected query and reopen the recovery dialog instead of cancelling it.
 */
export const buildMappingCancelUrl = (href: string): string => {
  const url = new URL(stripMappingErrorParam(href));
  const moduleMatch = url.pathname.match(/^(.+)\/(?:Helios|Flow)\//);
  if (moduleMatch) {
    url.pathname = moduleMatch[1];
  }
  return url.toString();
};

const toLocationParts = (location: { pathname: string; search: string; hash: string }) =>
  new URL(`${location.pathname}${location.search}${location.hash}`, 'https://mapping.local');

/**
 * buildMappingCancelUrl for in-app navigation: takes and returns router
 * location parts. Returns null when no owning module can be derived (e.g.
 * /user/settings) — callers should fall back to a history back-navigation or
 * a safe default route so the dialog can actually dismiss.
 */
export const mappingCancelLocation = (location: { pathname: string; search: string; hash: string }) => {
  const url = toLocationParts(location);
  const cancelled = new URL(buildMappingCancelUrl(url.href));
  if (cancelled.pathname === url.pathname) return null;
  return { pathname: cancelled.pathname, search: cancelled.search, hash: cancelled.hash };
};

/**
 * stripMappingErrorParam for in-app navigation: takes and returns router
 * location parts so a successful remap clears recovery without a reload.
 */
export const stripMappingErrorLocation = (location: { pathname: string; search: string; hash: string }) => {
  const stripped = new URL(stripMappingErrorParam(toLocationParts(location).href));
  return { pathname: stripped.pathname, search: stripped.search, hash: stripped.hash };
};

/**
 * navigate(-1) no-ops on a direct load (history idx 0), which would leave an
 * always-open dialog stuck on screen — fall back to the user index instead.
 */
export const navigateBackOrFallback = (navigate: NavigateFunction, fallbackPath = '/user') => {
  if ((window.history.state?.idx ?? 0) > 0) {
    navigate(-1);
  } else {
    navigate(fallbackPath, { replace: true });
  }
};

export const updateRedirectUri = (uri: string | undefined) => {
  if (!uri) return undefined;
  try {
    const url = new URL(uri);
    const params = new URLSearchParams(url.search);
    const redirectUri = params.get('redirect_uri');

    if (redirectUri) {
      const newRedirectUri = redirectUri.replace('/googleoauth2', '/google-mapping-redirect');
      return overrideRedirectUri(uri, newRedirectUri);
    }
    return uri;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse URI:', e);
    return uri;
  }
};
